<template>
  <Loading v-if="componentLoading" :style="style" />
  <Chart v-else class="pie-doughnut-chart" :option="chartConfig" :style="style"></Chart>
</template>

<script setup lang="ts">
import Chart from "@p-v/packages/Charts/BaseChart/index.vue"
import { ref } from "vue"
import { cloneDeep, defaultsDeep } from "lodash-es"
import defaultOption from "./config"
import Loading from "@p-v/packages/Loading.vue"
import { EChartsOption } from "echarts"

export interface PercentageData {
  title?: string // 标题
  percent?: number // 百分比
  curData?: number // 当前数据
  maxData?: number // 最大数据
}

export interface TextStyle {
  fontSize: number // 字体大小
  color: string // 字体颜色
  fontWeight: number // 字体粗细
  top?: number // 上边距
  left?: number // 左边距
  bottom?: number // 下边距
  right?: number // 右边距
}

export interface PercentageConfig {
  startAngle?: number // 开始角度
  clockwise?: boolean // 是否顺时针
  color?: { percent: number; color: string }[] // 渐变颜色配置
  background?: string // 背景颜色
  radius?: number // 内外圈半径
  width?: number // 环形宽度
  dataStyle?: TextStyle // 百分比配置
  titleStyle?: TextStyle // 标题配置
}

export interface PercentageChartProps {
  data?: PercentageData // 两条线的数据
  config?: PercentageConfig // 配置项
  style?: { [key: string]: string } // 配置项
}

const componentLoading = ref<boolean>(true)
// 引入默认模板配置
const chartConfig = ref<EChartsOption>(cloneDeep(defaultOption))

// 定义props
const props = withDefaults(defineProps<PercentageChartProps>(), {
  data: () => ({
    title: "",
    percent: undefined,
    curData: 0,
    maxData: 0,
  }),
  config: () => ({}),
  style: () => ({}),
})

const deg_to_pi = Math.PI / 180

function Point(r: number, deg: number) {
  return {
    x: r * Math.cos(deg_to_pi * deg),
    y: r * Math.sin(deg_to_pi * deg),
  }
}

function hexToRgb(hex: string): string {
  // 去掉开头的#号
  hex = hex.replace(/^#/, "")

  // 处理三位数的情况
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map(char => char + char)
      .join("")
  }

  // 解析红、绿、蓝值
  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `rgb(${r}, ${g}, ${b})`
}

const Color = (fromColor: string, toColor: string, percent: number) => {
  let FromRGB = fromColor
  let ToRGB = toColor
  if (fromColor.startsWith("#")) {
    FromRGB = hexToRgb(fromColor)
  }
  if (toColor.startsWith("#")) {
    ToRGB = hexToRgb(toColor)
  }
  const f = FromRGB.replace(/(?:\(|\)|rgb|RGB)*/g, "").split(",")
  const t = ToRGB.replace(/(?:\(|\)|rgb|RGB)*/g, "").split(",")
  const R = parseInt(f[0]) + (parseInt(t[0]) - parseInt(f[0])) * percent,
    G = parseInt(f[1]) + (parseInt(t[1]) - parseInt(f[1])) * percent,
    B = parseInt(f[2]) + (parseInt(t[2]) - parseInt(f[2])) * percent
  return "rgb(" + R + "," + G + "," + B + ")"
}

const pieDraw = (
  startAngle: number,
  radius: number,
  width: number,
  color: { percent: number; color: string }[],
  clockwise = true,
): Promise<string> => {
  //判断有没有percent为0的颜色，如果没有，就插入一个白色
  let hasZero = color.some(item => item.percent === 0)
  if (!hasZero) {
    color.unshift({ percent: 0, color: "#fff" })
  }
  // 从小到大排序
  color.sort((a, b) => a.percent - b.percent)
  const innerRadius = radius - width

  const diameter = radius * 2

  // 创建一个离屏 canvas 元素
  const offscreenCanvas = document.createElement("canvas")
  offscreenCanvas.width = diameter
  offscreenCanvas.height = diameter
  const center = { x: radius, y: radius }
  const shape = 10
  // 获取绘图上下文
  const ctx = offscreenCanvas.getContext("2d")
  return new Promise((resolve, reject) => {
    if (!ctx) return reject("2d context not supported")
    ctx.translate(center.x, center.y)
    ctx.rotate(startAngle - Math.PI / 2)
    let lastI = 0
    for (let ci = 1; ci < color.length; ci++) {
      const endColor = color[ci].color
      const startColor = color[ci - 1].color
      const endPercent = color[ci].percent
      // const startPercent = color[i - 1].percent

      ctx.strokeStyle = Color(startColor, endColor, 0)
      //   console.log(startColor, endColor, Color(startColor, endColor, 0), ctx.strokeStyle)
      const r = diameter / 2
      const line_deg = (1 / shape) % 360
      // console.log(line_deg);

      const line_deg_len = 360 * endPercent
      const coloePiece = 100 / (line_deg_len - lastI)
      let piece = 1
      for (let i = lastI; i < line_deg_len; i++) {
        // const deg = (time-i);
        const deg1 = Math.max(line_deg - i - shape, -360) * (clockwise ? -1 : 1)
        const deg2 = Math.min(line_deg - i, 0) * (clockwise ? -1 : 1)
        const pointx = Point(innerRadius, deg2)
        const point1 = Point(r, deg1)
        const point2 = Point(r, deg2)
        const point3 = Point(innerRadius, deg1)
        ctx.beginPath()
        // ctx.fillStyle="white";
        ctx.fillStyle = Color(startColor, endColor, (coloePiece * piece) / 100)
        // console.log(startColor, endColor, ctx.fillStyle, (coloePiece * piece) / 100, piece)
        ctx.moveTo(pointx.x, pointx.y)
        ctx.lineTo(point2.x, point2.y)
        ctx.lineTo(point1.x, point1.y)
        ctx.lineTo(point3.x, point3.y)

        // ctx.arc(center.x, center.y, innerRadius, (Math.PI * deg2) / 180, (deg1 / 180) * Math.PI)
        // ctx.arc(center.x, center.y, r, (Math.PI * deg2) / 180, (deg1 / 180) * Math.PI)
        ctx.closePath()
        // ctx.stroke();
        ctx.fill()
        piece += 1
      }
      lastI = line_deg_len
    }
    // 导出成base64的png格式
    const base64Image = offscreenCanvas.toDataURL("image/png")
    const newImg = document.createElement("img")
    newImg.src = base64Image
    document.body.appendChild(newImg)
    return resolve(base64Image)
    // offscreenCanvas.toBlob(
    //   blob => {
    //     if (!blob) return reject("blob is null")

    //     const url = URL.createObjectURL(blob)
    //     console.log("blob", url)
    //     newImg.onload = () => {
    //       // 不再需要读取该 blob，因此释放该对象
    //       URL.revokeObjectURL(url)
    //     }

    //     resolve(url)
    //   },
    //   "image/png",
    //   1,
    // )
  })
}

let pieBackaground = ""

function componentPropEngine(propsData: PercentageChartProps): Promise<EChartsOption> {
  const prop = defaultsDeep(propsData, props, {
    data: {
      percent: undefined,
      curData: 0,
      maxData: 0,
    },
    config: {
      startAngle: 0,
      clockwise: true,
      color: [
        {
          percent: 1,
          color: "#fff",
        },
      ],
      background: "#333",
      radius: [160, 200],
      dataStyle: {
        fontSize: 40,
        color: "#FFF",
        fontWeight: 700,
      },
    },
  })
  const dataSet = [[1, prop.data?.percent || prop.data?.curData]]
  const _animationDuration = 1000
  const _animationDurationUpdate = 1000
  const _animationEasingUpdate = "quarticInOut"
  let _valOnRadianMax = prop.data?.maxData || 100
  if (prop.data?.percent >= 0) {
    _valOnRadianMax = 1
  }
  const _outerRadius = prop.config?.radius || 200
  const _width = prop.config?.width || 40
  return new Promise(resolve => {
    const startAngle = (prop.config?.startAngle / 180) * Math.PI
    pieDraw(startAngle, _outerRadius, _width, prop.config?.color || [], prop.config?.clockwise).then(res => {
      pieBackaground = res
      function renderItem(params, api): any {
        const valOnRadian = api.value(1)
        const coords = api.coord([api.value(0), valOnRadian])
        const polarEndRadian = coords[3]
        const imageStyle = {
          image: pieBackaground,
          x: params.coordSys.cx - _outerRadius,
          y: params.coordSys.cy - _outerRadius,
          width: _outerRadius * 2,
          height: _outerRadius * 2,
        }
        const sectorOpt = {
          clockwise: prop.config?.clockwise,
          startAngle: startAngle - Math.PI / 2,
          endAngle: -polarEndRadian + startAngle - Math.PI / 2,
          enterFrom: { endAngle: startAngle - Math.PI / 2 },
        }
        if (!prop.config?.clockwise) {
          sectorOpt.enterFrom = { endAngle: startAngle - Math.PI / 2 }
          sectorOpt.endAngle = startAngle - Math.PI / 2 + polarEndRadian
          sectorOpt.startAngle = startAngle - Math.PI / 2
        }
        const children = [
          {
            type: "circle",
            shape: {
              cx: params.coordSys.cx,
              cy: params.coordSys.cy,
              r: _outerRadius - 1,
            },
            style: {
              fill: prop.config?.background || "rgba(0,0,0,1)",
            },
            clipPath: {
              type: "sector",
              shape: {
                cx: params.coordSys.cx,
                cy: params.coordSys.cy,
                r: _outerRadius - 1,
                r0: _outerRadius - _width + 1,
                startAngle: 0,
                endAngle: Math.PI * 2,
              },
            },
          },
          {
            type: "image",
            style: imageStyle,
            clipPath: {
              type: "sector",
              shape: {
                cx: params.coordSys.cx,
                cy: params.coordSys.cy,
                r: _outerRadius,
                r0: _outerRadius - _width,
                transition: "endAngle",
                ...sectorOpt,
              },
            },
          },
          {
            type: "image",
            style: imageStyle,
            clipPath: {
              type: "polygon",
              shape: {
                points: makePionterPoints(params, polarEndRadian),
              },
              extra: {
                polarEndRadian: polarEndRadian,
                transition: "polarEndRadian",
                enterFrom: { polarEndRadian: 0 },
              },
              during: function (apiDuring) {
                apiDuring.setShape("points", makePionterPoints(params, apiDuring.getExtra("polarEndRadian")))
              },
            },
          },
        ] as any[]
        const percentBlock = {
          type: "text",
          extra: {
            valOnRadian: valOnRadian,
            transition: "valOnRadian",
            enterFrom: { valOnRadian: 0 },
          },
          style: {
            text: makeText(valOnRadian),
            fontSize: prop.config?.dataStyle?.fontSize || 50,
            fontWeight: prop.config?.dataStyle?.fontWeight || 700,
            x: params.coordSys.cx,
            y: params.coordSys.cy,
            fill: prop.config?.dataStyle?.color || "#FFF",
            align: "center",
            verticalAlign: "middle",
            enterFrom: { opacity: 0 },
          },
          during: function (apiDuring) {
            apiDuring.setStyle("text", makeText(apiDuring.getExtra("valOnRadian")))
          },
        }
        if (prop.config?.dataStyle?.top) {
          percentBlock.style.y = prop.config?.dataStyle?.top
        } else if (prop.config?.dataStyle?.bottom) {
          percentBlock.style.y = params.coordSys.cy * 2 - prop.config?.dataStyle?.bottom
        }
        if (prop.config?.dataStyle?.left) {
          percentBlock.style.x = prop.config?.dataStyle?.left
        } else if (prop.config?.dataStyle?.right) {
          percentBlock.style.x = params.coordSys.cx * 2 - prop.config?.dataStyle?.right
        }

        children.push(percentBlock)
        if (prop.data?.title) {
          let x = params.coordSys.cx
          let y = params.coordSys.cy
          if (prop.config?.titleStyle?.top) {
            y = prop.config?.titleStyle?.top
          } else if (prop.config?.titleStyle?.bottom) {
            y = params.coordSys.cy * 2 - prop.config?.titleStyle?.bottom
          }
          if (prop.config?.titleStyle?.left) {
            x = prop.config?.titleStyle?.left
          } else if (prop.config?.titleStyle?.right) {
            x = params.coordSys.cx * 2 - prop.config?.titleStyle?.right
          }

          children.push({
            type: "text",
            style: {
              text: prop.data?.title,
              fontSize: prop.config?.titleStyle?.fontSize || 50,
              fontWeight: prop.config?.titleStyle?.fontWeight || 700,
              x,
              y,
              fill: prop.config?.titleStyle?.color || "#FFF",
              align: "center",
              verticalAlign: "middle",
              enterFrom: { opacity: 0 },
            },
          })
        }
        return {
          type: "group",
          children: children,
        }
      }
      function convertToPolarPoint(renderItemParams, radius, radian) {
        return [Math.cos(radian) * radius + renderItemParams.coordSys.cx, -Math.sin(radian) * radius + renderItemParams.coordSys.cy]
      }
      function makePionterPoints(renderItemParams, polarEndRadian) {
        return [
          convertToPolarPoint(renderItemParams, _outerRadius, polarEndRadian),
          convertToPolarPoint(renderItemParams, _outerRadius, polarEndRadian + Math.PI * 0.03),
        ]
      }
      function makeText(valOnRadian) {
        // Validate additive animation calc.
        if (valOnRadian < -10) {
          alert("illegal during val: " + valOnRadian)
        }
        return ((valOnRadian / _valOnRadianMax) * 100).toFixed(0) + "%"
      }
      resolve({
        animationEasing: _animationEasingUpdate,
        animationDuration: _animationDuration,
        animationDurationUpdate: _animationDurationUpdate,
        animationEasingUpdate: _animationEasingUpdate,
        dataset: {
          source: dataSet,
        },
        tooltip: {},
        angleAxis: {
          type: "value",
          startAngle: 0,
          show: false,
          min: 0,
          max: _valOnRadianMax,
        },
        radiusAxis: {
          type: "value",
          show: false,
        },
        polar: {},
        series: [
          {
            type: "custom",
            coordinateSystem: "polar",
            renderItem: renderItem,
          },
        ],
      })
    })
  })
}

const loading = () => {
  componentLoading.value = true
}
const setData = (data: PercentageChartProps) => {
  if (!data) {
    return
  }
  componentPropEngine(data).then(result => {
    componentLoading.value = false
    chartConfig.value = result
  })
}
defineExpose({ setData, loading })
</script>

<style lang="scss" scoped></style>
